import React, { useState, useEffect, useRef } from "react";
import { AddressModel } from "../../../_models/data.address.model";
import Text from "../inputs/text";
import Row from "../../../styles/layout/row";
import Col from "../../../styles/layout/col";
import { Note } from "../../../styles/forms/inputs";
import { EventTargetType } from "../../../_types/form";
import { FormQuestionModuleType, FormQuestionType, useFormQuestions } from "../../../state/context/formQuestions.store";
import { ShowFormQuestion } from "../../../functions/helpers";

interface Props {
	name: string;
	readonly?: boolean;
	onChange: (e: EventTargetType) => void;
	addressLine1Error?: string;
	postcodeError?: string;
	boroughError?: string;
	value: AddressModel;
	singleCol?: boolean;
	canEditReadOnly?: boolean;
	note?: string;
	controlledUpdated?: boolean;
	required?: boolean;
	immediate?: boolean;
}

const AddressInput: React.FC<Props> = ({
	name,
	readonly = false,
	onChange,
	addressLine1Error,
	postcodeError,
	boroughError,
	value,
	singleCol,
	canEditReadOnly,
	note,
	controlledUpdated = false,
	required = true,
	immediate = false,
}) => {
	const { formQuestions } = useFormQuestions();

	let timer: any = null;

	const [state, setState] = useState(new AddressModel(value));
	const latestValue = useRef(new AddressModel(value));

	useEffect(() => {
		if (controlledUpdated) {
			const updatedValue = new AddressModel(value);
			latestValue.current = updatedValue;
			setState((prevState) => ({
				...prevState,
				...updatedValue,
			}));
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	useEffect(() => {
		setState((prevState) => ({
			...prevState,
			addressLine1Error,
			postcodeError,
			boroughError,
		}));
	}, [addressLine1Error, postcodeError, boroughError]);

	const handleChange = ({ target }) => {
		latestValue.current = new AddressModel({ ...latestValue.current, [target.name]: target.value });
		setState((prevState) => ({
			...prevState,
			[target.name]: target.value,
		}));

		const notifyChange = () => onChange({
			target: { value: latestValue.current, type: "address", name },
		});
		if (immediate) {
			notifyChange();
			return;
		}
		clearTimeout(timer);
		timer = setTimeout(notifyChange, 500);
	};

	return (
		<>
			{note ? <Note>{note}</Note> : null}
			<Row>
				<Col span={singleCol ? 12 : 4} span1024={6} span768={12}>
					<Text
						name="addressLine1"
						label="Address Line 1"
						value={state.addressLine1}
						onChange={handleChange}
						error={state.addressLine1Error}
						readonly={readonly ? true : false}
						canEditReadOnly={canEditReadOnly}
						maxLength={255}
						required={required}
					/>
				</Col>
				<Col span={singleCol ? 12 : 4} span1024={6} span768={12}>
					<Text
						name="addressLine2"
						label="Address Line 2"
						value={state.addressLine2}
						onChange={handleChange}
						readonly={readonly ? true : false}
						canEditReadOnly={canEditReadOnly}
						maxLength={255}
					/>
				</Col>
				<Col span={singleCol ? 12 : 4} span1024={6} span768={12}>
					<Text
						name="addressLine3"
						label="Address Line 3"
						value={state.addressLine3}
						onChange={handleChange}
						readonly={readonly ? true : false}
						canEditReadOnly={canEditReadOnly}
						maxLength={255}
					/>
				</Col>
			</Row>
			<Row>
				<Col span={singleCol ? 12 : 4} span1024={6} span768={12}>
					<Text name="city" label="City" value={state.city} onChange={handleChange} readonly={readonly ? true : false} canEditReadOnly={canEditReadOnly} maxLength={255} />
				</Col>
				<Col span={singleCol ? 12 : 4} span1024={6} span768={12}>
					<Text name="county" label="County" value={state.county} onChange={handleChange} readonly={readonly ? true : false} canEditReadOnly={canEditReadOnly} maxLength={255} />
				</Col>
				<Col span={singleCol ? 12 : 4} span1024={6} span768={12}>
					<Text
						name="postcode"
						label="Postcode"
						value={state.postcode}
						onChange={handleChange}
						error={state.postcodeError}
						readonly={readonly ? true : false}
						canEditReadOnly={canEditReadOnly}
						maxLength={50}
						required={required}
					/>
				</Col>
				{ShowFormQuestion(formQuestions, FormQuestionModuleType.candidate, FormQuestionType.candidateBorough) ? (
					<Col span={singleCol ? 12 : 4} span1024={6} span768={12}>
						<Text
							name="borough"
							label="Borough"
							value={state.borough}
							onChange={handleChange}
							readonly={readonly ? true : false}
							error={state.boroughError}
							canEditReadOnly={canEditReadOnly}
							maxLength={255}
							required={required}
						/>
					</Col>
				) : null}
			</Row>
		</>
	);
};
export default AddressInput;
